import React, { useEffect, useState } from 'react';
import { DeleteOutlined, EditOutlined, PrinterOutlined, SnippetsOutlined, PlusOutlined } from '@ant-design/icons';
import { Divider, message, Empty, Button, Skeleton, notification, Tag, Collapse, Typography, Card, Table } from 'antd';
import dayjs from 'dayjs';
import { Link } from 'react-router-dom';

import api from '../../../utils/api';
import { QuotePreview } from '../../../components/Quotes';
import { ColumnProps } from 'antd/es/table';
import { QuoteProps } from '@partsbadger/types';
import { Price, ErrorRender } from '@partsbadger/library';
import { useHistory, useParams } from 'react-router-dom';

const { Title, Text } = Typography;

interface Props {
    parent_quote: number;
}

interface PartProps {
    name: string;
}

const QuoteRevisions: React.FC<Props> = ({ parent_quote }) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [quotes, setQuotes] = useState<any[]>([]);
    const [preview, setPreview] = useState<number | null>(null);
    const history = useHistory();

    const loadQuotes = async () => {
        setLoading(true);

        await api.Quotes.all({
            params: {
                duplicated_from: parent_quote,
            },
        })
            .then(data => {
                setQuotes(data.results);
                setLoading(false);
            })
            .catch(error => {
                notification.error({
                    duration: 10,
                    message: 'Error',
                    description: <ErrorRender error={error} />,
                    placement: 'bottomRight',
                });

                setLoading(false);
            });
    };

    useEffect(() => {
        if (parent_quote) {
            loadQuotes();
        }
    }, [parent_quote]);

    const columns: ColumnProps<QuoteProps>[] = [
        {
            width: '100%',
            render: record => {
                return (
                    <div className="quote-modification-list d-flex flex-row w-100">
                        <div className="d-flex flex-column w-100 justify-content-between">
                            <div className="d-flex flex-row align-items-baseline mb-1">
                                {!record.editable && <Tag color="red">Ordered</Tag>}
                                <Tag color="blue">Revision {record.revision}</Tag>
                                <span className="font-size-base">{record.name}</span>
                                <div className="font-size-md ml-auto">
                                    <Price value={record.grand_total} />
                                </div>
                            </div>

                            <div className="text-muted mb-1">
                                Created on{' '}
                                {record.created_time
                                    ? dayjs(record.created_time).format('YYYY-MM-DD')
                                    : record.created_time}{' '}
                                by {record.created_by}
                            </div>

                            <div className="d-flex flex-column flex-sm-row align-items-sm-center align-items-start justify-content-between w-100">
                                <Collapse ghost style={{ margin: 0, padding: 0 }} className="mb-3 mb-sm-0">
                                    <Collapse.Panel
                                        header={`Parts (${record.parts.length})`}
                                        key="1"
                                        style={{ margin: 0, padding: 0 }}
                                    >
                                        {record.parts.map((item: PartProps, index: number) => {
                                            return (
                                                <>
                                                    {index !== 0 ? <Divider className="m-0 my-1 p-0" /> : null}
                                                    <Text key={index} className="d-block">
                                                        {item.name}
                                                    </Text>
                                                </>
                                            );
                                        })}
                                    </Collapse.Panel>
                                </Collapse>

                                <div className="d-flex flex-row align-items-start h-100" style={{ columnGap: '6px' }}>
                                    {record.editable && (
                                        <>
                                            <Link to={`/my-account/quotes/${record.id}/order/`}>
                                                <Button type="default" className="ant-btn-xs" icon={<EditOutlined />}>
                                                    Edit
                                                </Button>
                                            </Link>

                                            <Button
                                                style={{ width: '87.7px' }}
                                                type="default"
                                                className="ant-btn-xs"
                                                onClick={async () => {
                                                    await api.Quotes.deleteQuote(record.id)
                                                        .then(data => {
                                                            message.success(`Revision ${record.name} deleted`);
                                                            loadQuotes();
                                                        })
                                                        .catch(err => {
                                                            notification.error({
                                                                duration: 10,
                                                                message: 'Error',
                                                                description: <ErrorRender error={err} />,
                                                                placement: 'bottomRight',
                                                            });
                                                        });
                                                }}
                                                icon={<DeleteOutlined />}
                                            >
                                                Delete
                                            </Button>
                                        </>
                                    )}

                                    <Button
                                        type="default"
                                        className="ant-btn-xs"
                                        onClick={() => setPreview(record.id)}
                                        style={{ width: '87.7px' }}
                                        icon={<PrinterOutlined />}
                                    >
                                        Print
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            },
        },
    ];

    //If there is no qoutes let the use know that he has no created revisions yet
    if(!loading && quotes.length === 0) {
        return (
            <div style={{
                marginTop: '20px',
            }}>
                <Empty
                  image={<SnippetsOutlined style={{
                      fontSize:70
                  }} />}
                  description={<div>
                      <div style={{ color: '#000' }}>
                          You have not created any revisions yet.
                      </div>
                      <div style={{
                          marginTop: '10px',
                      }}>
                          <Button icon={<PlusOutlined/> } type={"primary"} onClick={()=>{
                              api.Quotes.newRevision(parent_quote)
                                .then(data => {
                                    message.success(`Revision ${data.name} created`);
                                    history.push(`/my-account/quotes/${data.id}/order/`);
                                })
                                .catch(error => {
                                    message.error('Something went wrong!');
                                });
                          }}>Create a New Modification</Button>
                      </div>

                  </div>}
                />
            </div>
        );
    }

    return (
        <>
            <Table
                showHeader={false}
                columns={columns}
                loading={loading}
                dataSource={quotes}
                pagination={false}
                tableLayout="fixed"
            />

            <QuotePreview quote_id={preview} open={!!preview} onClose={() => setPreview(null)} />
        </>
    );
};

export default QuoteRevisions;
